import { Button, Paper, Select, Stack, Table, Textarea } from "@mantine/core"
import { modals } from "@mantine/modals"
import { notifications } from "@mantine/notifications"
import { useMutation } from "@tanstack/react-query"
import { use, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { ButtonDetails, ModalControls, SortHeader, TeamFilter } from "@/components/controls"
import { useActivity, useAnonymous, useAPIResources, useAuth, useQueryState, useUsers } from "@/hooks"
import type { IMetaSurvey, TUser } from "@/types"
import type { TSession, TUserSession } from "@/types/activities"
import {
  buildUserSessionURL,
  createAPISurveyReminder,
  hasAdmin,
  surveyReminder,
  surveyStatsUserSessionsAPI,
  UserSessionContext,
  usersessionAPI,
} from "@/utils"
import { isOver } from "@/utils/session"
import { StateFilter } from "../controls/StateFilter"
import { UserLine } from "../people"
import { useModalUserSessionDetails } from "../surveys/details/ModalUserSessionDetails"
import { AdminOnly, DateTime, Score } from "../utils"
import { SessionProgress } from "./SessionProgress"
import { SessionState } from "./SessionState"
import { UserSessionsFilters } from "./UserSessionsFilters"
import { UserSessionsTable } from "./UserSessionsTable"

interface IProps {
  session: TSession
  withFilters?: boolean
  withProgress?: boolean
  fallback?: React.ReactNode
}

export function SessionUserSessions({ session, withProgress, withFilters, fallback }: IProps) {
  const { t } = useTranslation()

  const teams = TeamFilter.useValue()
  const where = { session_id: session.id, team_id: teams?.length ? { $in: teams } : undefined }
  const [sort = "-finished", _] = useQueryState<string>("sort")

  const { user } = useAuth()
  const bindToTeams = hasAdmin(user) ? [] : user?.team_ids || []
  if (bindToTeams.length > 0) where.team_id = { $in: bindToTeams }

  const withScore = session.type == "pulse" || session.type == "survey"
  const { data: { data: rawData = [] } = {} } = useAPIResources(usersessionAPI, { where })
  const { data: { data: rawStats = [] } = {} } = useAPIResources(surveyStatsUserSessionsAPI, {
    enabled: withScore && !!rawData.length,
    where: {
      id: {
        $in: rawData
          .filter((ses) => !!ses.finished)
          .map((ses) => ses.id)
          .sort(),
      },
    },
  })
  const stats = rawStats.reduce((acc, stat) => {
    acc[stat.usersession_id] = stat
    return acc
  }, {})
  const scoreColumn = rawStats.length > 0
  const { findUser } = useUsers()

  const state = StateFilter.useValue()
  const userId = useQueryState<string>("user")[0]

  return (
    <Stack gap="xs">
      {withFilters && <UserSessionsFilters session={session} data={rawData} />}
      {withProgress && <SessionProgress data={rawData} />}
      <Paper withBorder>
        <UserSessionsTable
          where={where}
          fallback={fallback}
          refetchInterval={session.state == "in_progress" ? 60e3 : false}
          headers={[
            <SortHeader key="full_name" field="full_name" miw={400}>
              {t("Participant")}
            </SortHeader>,
            ...(scoreColumn
              ? [
                  <SortHeader key="score" field="score" justify="flex-end">
                    {t("Score")}
                  </SortHeader>,
                ]
              : []),
            <SortHeader key="state" field="state">
              {t("State")}
            </SortHeader>,
            <SortHeader key="finished" field="finished" defaultActive>
              {t("Finished")}
            </SortHeader>,
            "",
          ]}
          transform={(usersessions) => {
            const desc = sort?.startsWith("-")
            const fieldSort = sort?.startsWith("-") ? sort.slice(1) : sort

            if (fieldSort && ["full_name", "score", "state", "finished"].includes(fieldSort)) {
              usersessions = usersessions.toSorted((a, b) => {
                let aValue: string | number = ""
                let bValue: string | number = ""

                if (fieldSort == "full_name") {
                  const userA = findUser(a.user_id)!
                  const userB = findUser(b.user_id)!
                  aValue = userA?.full_name || userA?.name
                  bValue = userB?.full_name || userB?.name
                } else if (fieldSort == "score") {
                  aValue = stats[a.id]?.score || 0
                  bValue = stats[b.id]?.score || 0
                } else if (fieldSort == "state") {
                  aValue = a.state
                  bValue = b.state
                } else if (fieldSort == "finished") {
                  aValue = a.finished || ""
                  bValue = b.finished || ""
                }

                if (aValue < bValue) return desc ? 1 : -1
                if (aValue > bValue) return desc ? -1 : 1
                return 0
              })
            }

            // Filter by State
            if (state.length > 0) usersessions = usersessions.filter((ses) => state.includes(ses.state))

            // Filter by user
            if (userId) usersessions = usersessions.filter((ses) => ses.user_id == userId)

            return usersessions
          }}
        >
          <UserSessionRow stats={scoreColumn ? stats : undefined} />
        </UserSessionsTable>
      </Paper>
    </Stack>
  )
}

function UserSessionRow({ stats }: { stats?: Record<string, { score: number }> }) {
  const { t } = useTranslation()
  const usersession = use(UserSessionContext)!
  const score = stats?.[usersession.id]?.score
  const openDetails = useModalUserSessionDetails(usersession)
  const { findUser } = useUsers()
  const anonymous = useAnonymous()
  const user = findUser(usersession.user_id) || anonymous

  return (
    <>
      <Table.Td>
        <UserLine user={user} url={buildUserSessionURL(usersession)} isActive={user.is_active} />
      </Table.Td>
      {stats && (
        <Table.Td ta="right">
          <Score text value={score} />
        </Table.Td>
      )}
      <Table.Td>
        <SessionState state={usersession.state} />
      </Table.Td>
      <Table.Td>
        <DateTime timestamp={usersession.finished} />
      </Table.Td>
      <Table.Td ta="right">
        {usersession.state == "finished" && <ButtonDetails onClick={openDetails} />}
        <AdminOnly>
          {usersession.state == "pending" && !isOver(usersession.session) && (
            <Button
              size="compact-md"
              onClick={() => {
                modals.open({
                  title: t("Send reminder"),
                  children: <SendReminder user={user} usersession={usersession} />,
                })
              }}
            >
              {t("Send reminder")}
            </Button>
          )}
        </AdminOnly>
      </Table.Td>
    </>
  )
}

function SendReminder({ user, usersession }: { user: TUser; usersession: TUserSession }) {
  const { t } = useTranslation()
  const { data: activity } = useActivity(usersession.activity_id)
  const reminders = (activity?.meta as IMetaSurvey | undefined)?.reminders
  const hasReminders = reminders && reminders.length > 0

  const [message, setMessage] = useState<string>(t(surveyReminder))

  useEffect(() => {
    if (hasReminders) {
      setMessage(reminders[0]!)
    }
  }, [hasReminders, reminders])

  const send = useMutation({
    mutationFn: createAPISurveyReminder,
    onSuccess: () => {
      notifications.show({
        title: t("Reminder"),
        message: t("A reminder has been sent to {{name}}.", { name: user.full_name || user.name }),
        color: "green",
      })
    },
  })

  return (
    <Stack>
      <UserLine user={user} />
      {hasReminders && reminders.length > 1 && (
        <Select
          label={t("Reminder")}
          data={reminders.map((r, i) => ({ value: r, label: `${t("Reminder")} ${i + 1}` }))}
          value={message}
          onChange={(value) => value && setMessage(value)}
        />
      )}
      <Textarea
        label={t("Message")}
        autosize
        maxRows={10}
        value={message}
        onChange={(e) => setMessage(e.currentTarget.value)}
      />
      <ModalControls
        label={t("Send")}
        onSubmit={() => {
          send.mutate({ id: usersession.session_id, usersessionId: usersession.id, message })
          modals.closeAll()
        }}
      />
    </Stack>
  )
}
